<script setup lang="ts">
import { computed } from 'vue';
import { useTrip } from '@/stores/trip';
import { computeRouteTotals } from '@/core/totals';
import { fmtTimeCompact } from '@/core/format';
import type { TripMode } from '@/types';

/**
 * Carte mini-comparatif : train vs bus vs mixte.
 * Recalcule les totaux pour chaque mode et affiche une barre de comparaison.
 * Cliquer sur une ligne (qui n'est pas le mode courant et est atteignable)
 * bascule le store sur ce mode.
 */
const trip = useTrip();

const SCENARIOS = [
  { key: 'train' as TripMode, label: 'Train', icon: 'ti-train' },
  { key: 'bus' as TripMode, label: 'Bus', icon: 'ti-bus' },
  { key: 'best' as TripMode, label: 'Mixte', icon: 'ti-route' },
];

const rows = computed(() => {
  if (trip.state.routeStops.length < 2) return [];
  const data = SCENARIOS.map((sc) => ({
    ...sc,
    data: computeRouteTotals(trip.state.routeStops, sc.key),
  }));
  const reachable = data.filter(
    (sc) => !sc.data.unreachable && sc.data.totalSegs > 0,
  );
  if (reachable.length === 0) return [];
  const maxTime = Math.max(...reachable.map((sc) => sc.data.totalMin));
  // Le mode 'night' partage la même comparaison que 'best' (l'UI fallback dessus)
  const currentMode = trip.state.routeMode === 'night' ? 'best' : trip.state.routeMode;

  return data.map((sc) => {
    const d = sc.data;
    const unreachable = d.unreachable || d.totalSegs === 0;
    return {
      ...sc,
      isCurrent: sc.key === currentMode,
      unreachable,
      fillPct: unreachable ? 0 : d.totalMin / maxTime,
      display: unreachable ? '—' : fmtTimeCompact(d.totalMin),
    };
  });
});

function onRowClick(key: TripMode, isCurrent: boolean, unreachable: boolean) {
  if (isCurrent || unreachable) return;
  trip.setMode(key);
}
</script>

<template>
  <div
    v-if="rows.length > 0"
    class="p-4 bg-surface border border-DEFAULT rounded-md"
  >
    <h3 class="flex items-center gap-2 text-sm font-medium mb-3">
      <i class="ti ti-arrows-left-right text-accent" aria-hidden="true"></i>
      Comparer les modes
    </h3>
    <div class="space-y-2">
      <button
        v-for="r in rows"
        :key="r.key"
        type="button"
        :disabled="r.isCurrent || r.unreachable"
        :class="[
          'w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-160',
          r.isCurrent && 'bg-accent/10',
          !r.isCurrent && !r.unreachable && 'hover:bg-surface-muted/60 cursor-pointer',
          r.unreachable && 'opacity-50 cursor-not-allowed',
        ]"
        :title="
          r.isCurrent
            ? 'Mode actuel'
            : r.unreachable
              ? 'Inaccessible avec ce mode'
              : `Cliquer pour basculer en ${r.label}`
        "
        @click="onRowClick(r.key, r.isCurrent, r.unreachable)"
      >
        <i :class="['ti', r.icon, 'text-base']" aria-hidden="true"></i>
        <span class="font-medium min-w-[55px] text-left">{{ r.label }}</span>
        <span class="flex-1 h-1.5 bg-surface-muted rounded-full overflow-hidden">
          <span
            class="block h-full bg-accent rounded-full transition-all duration-300"
            :style="{ width: `${r.fillPct * 100}%` }"
          ></span>
        </span>
        <span class="font-mono font-medium min-w-[55px] text-right">{{ r.display }}</span>
      </button>
    </div>
  </div>
</template>
